import React, { Component } from 'react';
import DockPanel from './DockPanel/DockPanel';
import {Button, Tabs, Tab, Table, Spinner, Form, InputGroup, Alert} from "react-bootstrap";
import ProjectSelector from './ProjectSelector';
import MarkdownPreview from '@uiw/react-markdown-preview';
import StatsPanel from "./StatsPanel";

class ProjectModal extends Component {

  constructor(props) {
    super(props);

    this.state = {
      activeTab: props.editionId ? 'edition_metadata' : 'project_data',
      detailsModalLoading: false,
      editionEvents: [],
      transferTo: '',
      transferLoading: false,
      transferError: null,
      transferSuccess: false,
    };
  }

  getEditionEventHistory = async (editionId) => {
    try {
      const { contract, tokenId, fromBlock } = this.props;
      if (!contract || tokenId === null || tokenId === undefined || fromBlock === null || fromBlock === undefined) {
        this.setState({ editionEvents: [], detailsModalLoading: false });
        return;
      }

      const events = await contract.getPastEvents('allEvents', {
        filter: { tokenId: tokenId },
        fromBlock: fromBlock,
        toBlock: 'latest'
      });

      const editionEvents = events.filter(e => String(e.returnValues.tokenId) === String(tokenId));

      this.setState({
        editionEvents,
        detailsModalLoading: false,
      });
    } catch (error) {
      console.error(error);
      this.setState({ detailsModalLoading: false });
    }
  }

  refreshEditionData = async () => {
    this.setState({ detailsModalLoading: true });
    await this.getEditionEventHistory(this.props.editionId);
    this.setState({ detailsModalLoading: false });
  }

  handleTransfer = async () => {
    const { contract, account, tokenId } = this.props;
    const { transferTo } = this.state;

    if (!transferTo || !transferTo.match(/^0x[0-9a-fA-F]{40}$/)) {
      this.setState({ transferError: 'Please enter a valid Ethereum address.' });
      return;
    }

    this.setState({ transferLoading: true, transferError: null, transferSuccess: false });

    try {
      await contract.methods.safeTransferFrom(account, transferTo, tokenId)
        .send({ from: account })
        .once('receipt', () => {
          this.setState({ transferLoading: false, transferSuccess: true, transferTo: '' });
          setTimeout(() => window.location.reload(), 2000);
        });
    } catch (error) {
      console.error(error);
      const msg = (error.message && error.message.length < 200) ? error.message : 'Transaction failed or was rejected.';
      this.setState({ transferLoading: false, transferError: msg });
    }
  }

  componentDidMount() {
    if (this.props.editionId) {
      try {
        this.refreshEditionData();
      } catch(error) { console.error(error) }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.editionId !== this.props.editionId) {
      this.setState({
        activeTab: this.props.editionId ? 'edition_metadata' : 'project_data',
        transferTo: '', transferLoading: false, transferError: null, transferSuccess: false,
      });
      if (this.props.editionId) {
        this.refreshEditionData();
      }
    }
    if (prevProps.editionId === this.props.editionId && prevProps.metadataObject !== this.props.metadataObject) {
      if (this.props.editionId) {
        this.refreshEditionData();
      }
    }
  }

  render() {
    const { show, onHide,
            account, userIsAdmin,
            projects, projectId, editionId, projectTokens,
            projectIdToArtistAddress, onProjectChange,
            selectedProjectInformation, contract, platform,
            script, pagePosts,
            metadataObject, compileMetadata, compileMetadataProgress, openStatsViewer,
            ownerOfSelectedEdition,
            tokenId,
            clientViewImageURLInDetailView } = this.props;
    const { activeTab } = this.state;

    const isEditionView = !!editionId;

    // Find the info post (filter out hidden posts, then find last markdown match)
    const posts = pagePosts || [];
    const hiddenTxHashes = new Set(posts.filter(p => p.tag && p.tag.startsWith('H')).map(p => p.tag.slice(1)));
    const visiblePosts = posts.filter(p => !hiddenTxHashes.has(p.transactionHash));
    const isInfoMarkdown = (content) => {
      if (!content || !/^.{0,10}#/m.test(content)) return false;
      const textOutsideFences = content.replace(/```[\s\S]*?```/g, '').trim();
      return textOutsideFences.length > 20;
    };
    // The tag is authoritative. Content-sniffing alone misreads a stylesheet
    // as the description, because CSS hex colours (#0d0d0d) look like markdown
    // headers to the check -- so a style post showed up as info text.
    const NON_INFO_TAGS = ['style.immutables.art', 'style', 'code'];
    const infoPosts = visiblePosts.filter(p =>
      NON_INFO_TAGS.indexOf(p.tag) < 0 && isInfoMarkdown(p.content));
    const infoPost = infoPosts.length > 0 ? infoPosts[infoPosts.length - 1] : null;

    // Edition metadata entry
    let metadataObjectEntry;
    if (isEditionView) {
      try {
        metadataObjectEntry = metadataObject[projectTokens[editionId - 1].transactionHash];
      } catch(error) { console.error(error) }
    }

    // Title
    const projectName = (projects[projectId - 1]) ? projects[projectId - 1].name : "";
    const title = isEditionView ? `${projectName} # ${parseInt(editionId)}` : (projectName || "Create Project");

    // Shared tab content
    const projectInfoTab = infoPost && (
      <Tab eventKey="project_info" title="Project Information">
        <p></p>
        <div className="info-post-content">
          <MarkdownPreview source={infoPost.content} />
        </div>
        <p></p>
      </Tab>
    );

    const projectCodeTab = (
      <Tab eventKey="project_code" title="Project Code">
        <p></p>
        {script ? (
          <pre style={{
            maxHeight: '60vh',
            overflow: 'auto',
            backgroundColor: '#1e1e1e',
            color: '#d4d4d4',
            padding: '1em',
            borderRadius: '4px',
            fontSize: '0.85em',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            <code>{script}</code>
          </pre>
        ) : (
          <p className="font-display">No script loaded.</p>
        )}
      </Tab>
    );

    const projectDataTab = (
      <Tab eventKey="project_data" title="Project Data">
        <p></p>
        <center>
        {selectedProjectInformation}
        </center>
        <p></p>
      </Tab>
    );

    const projectMetadataTab = (
      <Tab eventKey="project_metadata" title="Statistics">
      <p></p>
      <MarkdownPreview source={platform.aboutProjectMetadata} />
      <p></p>
        {(() => {
          // Count only this project's editions: metadataObject accumulates every
          // edition rendered this session, including other projects, which made
          // the tally exceed the project's own edition count.
          const metaCount = (metadataObject && projectTokens)
            ? projectTokens.filter(t => metadataObject[t.transactionHash]).length
            : 0;
          const tokenCount = projectTokens ? projectTokens.length : 0;
          return (
            <>
            <p><b>{metaCount}</b> of <b>{tokenCount}</b> editions have metadata captured.</p>
            <p>Compile metadata by rendering every page; the charts appear below.</p>
            {(() => {
              const isDone = compileMetadataProgress && compileMetadataProgress.startsWith('done:');
              const isCompiling = compileMetadataProgress && !isDone;
              return (
                <>
                {isCompiling && (
                  <p><Spinner animation="grow" size="sm" />&nbsp;
                  <span className="font-display">compiling {compileMetadataProgress}</span></p>
                )}
                {isDone && (
                  <p className="font-display">Compiled {compileMetadataProgress.split(':')[1]} editions with metadata.</p>
                )}
                <Button variant="outline-success" className="font-display" onClick={compileMetadata} disabled={tokenCount === 0 || isCompiling || isDone} style={{marginRight: '0.5em'}}>
                  <b>compile stats</b>
                </Button>
                <StatsPanel statsData={this.props.statsData} onSelectEdition={this.props.onSelectEdition} onFilterChange={this.props.onFilterChange} />
                </>
              );
            })()}
            </>
          );
        })()}
        <p></p>
        <Form.Group className="mb-3" controlId="exampleForm.ControlTextarea1">
          <Form.Label>Raw Metadata</Form.Label>
          <Form.Control as="textarea" defaultValue={JSON.stringify(metadataObject)} rows={10} />
        </Form.Group>
      </Tab>
    );

    // Lifted out of the render so it can be handed to the panel, which keeps
    // its actions in a strip below the body the way the dialog did.
    const panelFooter = (
      <>
          {isEditionView ? (
            <>
            <Button variant="info btn-sm" className="font-display" href={`#/${projectId}/${editionId}`}>permalink</Button>
            {(contract && contract.options) ? (
              <>
              <Button variant="outline-secondary btn-sm" className="font-display" onClick={() => window.open(`${platform.etherscan_token_link}${contract.options.address}${platform.etherscan_token_query_after_contract}${projectTokens[editionId-1].tokenId}`)}>etherscan</Button>
              </>
            ) : ("")}
            </>
          ) : (
            (contract && contract.options) ? (
              <>
              <Button variant="outline-secondary btn-sm" className="font-display" onClick={() => window.open(`${platform.etherscan_link}${contract.options.address}`)}>etherscan</Button>
              </>
            ) : ("")
          )}
          <Button variant="secondary btn-sm" className="font-display" onClick={onHide}>
            close
          </Button>
      </>
    );

    return (
      <DockPanel
        show={show}
        onClose={onHide}
        title={title}
        storageKey="project"
        defaultWidth={900}
        defaultHeight={660}
        minWidth={380}
        minHeight={260}
        footer={panelFooter}
      >
        <div className="dock-panel__pad">

        {isEditionView && <p>{platform.detailsModalAdditionalDetails}</p>}

        <ProjectSelector
            account={account}
            userIsAdmin={userIsAdmin}
            projects={projects}
            projectId={projectId}
            projectIdToArtistAddress={projectIdToArtistAddress}
            onProjectChange={onProjectChange}
        />

        <Tabs activeKey={activeTab} onSelect={(k) => this.setState({ activeTab: k })} id="project-modal-tabs">

          {isEditionView && (
          <Tab eventKey="edition_metadata" title="Metadata">
            <p></p>
              <div className="text-center">
                <div>
                { (!clientViewImageURLInDetailView) ? (
                    (metadataObjectEntry && ("transactionHash" in metadataObjectEntry)) ? (
                          <Table striped bordered hover responsive className='editionEventHistory' size="sm">
                            <thead>
                              <tr>
                                <th>Property</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                            {Object.keys(metadataObjectEntry).map((key, i) => (
                              <tr key={i}>
                                <th>{key}</th>
                                <td>{metadataObjectEntry[key]}</td>
                              </tr>
                            ))}
                            </tbody>
                          </Table>
                    ) : ("Metadata Not Currently Available.")
                  ) : ("You must view the live render to compute the metadata.")
                }
                </div>
              </div>
          </Tab>
          )}

          {isEditionView && (ownerOfSelectedEdition === account) && (
          <Tab eventKey="edition_transfer" title="Transfer Edition">
            <p></p>
            <p>Transfer <b>][art Token # {tokenId}</b> to another Ethereum address. This action is irreversible.</p>
            <Form onSubmit={(e) => { e.preventDefault(); this.handleTransfer(); }}>
              <Form.Group className="mb-3">
                <Form.Label className="font-display">Recipient Address</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="0x..."
                    value={this.state.transferTo}
                    onChange={(e) => this.setState({ transferTo: e.target.value, transferError: null, transferSuccess: false })}
                    disabled={this.state.transferLoading}
                  />
                </InputGroup>
              </Form.Group>

              {this.state.transferError && (
                <Alert variant="danger" className="font-display">{this.state.transferError}</Alert>
              )}
              {this.state.transferSuccess && (
                <Alert variant="success" className="font-display">Transfer complete. The page will reload momentarily.</Alert>
              )}

              <div className="text-center">
              <Button
                variant="warning"
                className="font-display"
                type="submit"
                disabled={this.state.transferLoading || this.state.transferSuccess || !this.state.transferTo}
              >
                {this.state.transferLoading ? (
                  <><Spinner animation="border" size="sm" />&nbsp;transferring...</>
                ) : (
                  <b>transfer edition</b>
                )}
              </Button>
              </div>
            </Form>
            <hr/>
            <p className="text-muted" style={{fontSize: '0.85em'}}>
              You can also transfer via Etherscan:&nbsp;
              <a href={`${platform.etherscan_link}${contract.options.address}#writeContract`} target="_new">
                safeTransferFrom on Etherscan
              </a>
            </p>
          </Tab>
          )}

          {projectDataTab}
          {projectInfoTab}
          {projectCodeTab}
          {projectMetadataTab}

        </Tabs>

        </div>
      </DockPanel>
    );
  }
}

export default ProjectModal;
